// Init error reporting {{{
import { initMonitoring } from 'monitoring';
initMonitoring();
// Init error reporting }}}

// todo check if this is actually needed on Windows

import 'normalize.css';
import 'react-virtualized/styles.css';
import './styles/app.less';
import React from 'react';
import { render } from 'react-dom';
import {
  initializeConnectionServices,
  onlineConnectionService,
} from '@neptune/shared/core-connection-domain';
import { initializeSso } from '@neptune/sso-business-logic';

import '@neptune/shared/venus-ui';

import { logger } from '@neptune/shared/core-logger-util';
import {
  authClient,
  getKeycloakInitOptions,
  isAuthServerReachable,
} from '@neptune/shared/core-auth-domain';

import { AppInitErrorPage } from '@neptune/shared/common-ui';
import { renderSplashScreen } from 'initialization/helpers';
import { handleFeatureFlags } from 'common/featureFlag';
import { localStorageMigrate } from './local-storage';
import { localStorageTopKey } from 'common/localStorage';
import { isBrowserAllowed } from '@neptune/shared/common-util';
import { BrowserNotAllowedInfo } from '@neptune/application-feature';
import { initializeTheme } from '@neptune/shared/theme-business-logic';
import * as Sentry from '@sentry/react';

initializeTheme();

if (!isBrowserAllowed()) {
  render(<BrowserNotAllowedInfo />, document.getElementById('app'));
  throw new Error('Browser not allowed.');
}

initializeConnectionServices();
handleFeatureFlags();
printBackendInstance();
renderSplashScreen();

authClient.onAuthRefreshError = async () => {
  onlineConnectionService.goOnline();

  if (authClient.authenticated) {
    // eslint-disable-next-line no-console
    console.warn('Authentication: Auth refresh error, auth client is authenticated');
    logger.logMessage('Authentication: Auth refresh error, auth client is authenticated');
    return;
  }

  if (!(await isAuthServerReachable())) {
    // eslint-disable-next-line no-console
    console.warn('Authentication: Auth refresh error, auth server is not reachable');
    logger.logMessage('Authentication: Auth refresh error, auth server is not reachable');
    return;
  }

  // eslint-disable-next-line no-console
  console.warn('Authentication: Auth refresh error, auth client is not authenticated');
  logger.logMessage('Authentication: Auth refresh error, auth client is not authenticated');

  await logger.flush();

  await authClient.login({ prompt: 'none' });
};

authClient.onAuthLogout = async () => {
  // eslint-disable-next-line no-console
  console.log('Authentication: Logout triggered');
  logger.logMessage('Authentication: Logout triggered');
};

const migrateLocalstorageToCurrentVersion = async () => {
  await localStorageMigrate(localStorageTopKey);
};

migrateLocalstorageToCurrentVersion()
  .then(async () => {
    await onlineConnectionService.ensureConnection();
  })
  .then(() => {
    Sentry.addBreadcrumb({
      category: 'authentication',
      level: 'info',
      message: 'Authentication: Initiating auth client',
      data: {
        startTime: sessionStorage.getItem('neptune-start-time'),
        now: new Date().toISOString(),
      },
    });
  })
  .then(() => authClient.init(getKeycloakInitOptions()))
  .then((authenticated) => {
    Sentry.addBreadcrumb({
      category: 'authentication',
      level: 'info',
      message: 'Authentication: Initiating auth client done',
      data: {
        authenticated,
        now: new Date().toISOString(),
        startTime: sessionStorage.getItem('neptune-start-time'),
      },
    });

    return authenticated;
  })
  .then(initialize)
  .catch(showError)
  .finally(() => {
    sessionStorage.removeItem('neptune-start-time');
  });

async function initialize(authenticated) {
  await initializeSso({ authenticated });

  const desktopInit = await import('initialization/desktop');
  await desktopInit.initializeDesktopApp(authenticated);
}

function showError(error) {
  logger.logMessage('Application initialization failed');

  if (error) {
    logger.logException(error, { module: 'index.js' });
  }

  render(<AppInitErrorPage />, document.getElementById('app'));
}

function printBackendInstance() {
  const neptuneInstance = process.env.NEPTUNE_INSTANCE;

  if (neptuneInstance) {
    // eslint-disable-next-line no-console
    console.log(`%cNeptune instance: ${neptuneInstance}`, 'color: #ff6600; font-size: 14px');
  }
}
